import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AnnotationDraft } from '@/apiClients/goldSet/annotation';
import type { GoldSetApi } from '@/apiClients/goldSet/api';
import type { ExplorerFilters } from '@/apiClients/goldSet/explorer';
import type { FixtureGoldSetRole } from '@/apiClients/goldSet/fixtureApi';
import type {
  GoldSetClaimNextInput,
  GoldSetFilters,
  GoldSetItem,
  GoldSetNominationInput,
} from '@/apiClients/goldSet/types';
import { ApiRegion } from '@/config';
import { UNSAFE_FEATURES } from '@/context/FeatureContext';
import { isLocalUiScenario } from '@/dev/localUiScenario';
import { useQueryNeverRefetch } from '@/utils';

let apiPromise: Promise<GoldSetApi> | null = null;
let apiSource: 'fixtures' | 'mnt' | null = null;

const currentSource = () => (isLocalUiScenario() || UNSAFE_FEATURES.gold_set_queue_fixture_data ? 'fixtures' : 'mnt');

/**
 * Role the fictional adapter plays, from `localStorage.gold_set_role` (default expert), so the
 * local scenario can show what a reviewer without the expert flag sees. Fixtures only.
 */
const scenarioRole = (): FixtureGoldSetRole => {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem('gold_set_role');
  return stored === 'reviewer' ? 'reviewer' : 'expert';
};

/**
 * MNT by default. The fictional fixture adapter serves the local UI scenario and the
 * `gold_set_queue_fixture_data` demo flag; it loads on demand so its data stays out of the main bundle.
 */
export const getGoldSetApi = () => {
  const source = currentSource();
  if (!apiPromise || apiSource !== source) {
    apiSource = source;
    apiPromise =
      source === 'fixtures'
        ? import('@/apiClients/goldSet/fixtureApi').then(m =>
            // Logged in with demo data (the flag): the food picker still searches the real catalog.
            m.createFixtureGoldSetApi({ role: scenarioRole(), demoCatalog: isLocalUiScenario() }),
          )
        : import('@/apiClients/goldSet/mntApi').then(m => m.createMntGoldSetApi());
  }
  return apiPromise;
};

/** Tests swap in their own adapter. */
export const setGoldSetApiForTests = (api: GoldSetApi | null) => {
  apiPromise = api ? Promise.resolve(api) : null;
  apiSource = api ? currentSource() : null;
};

export const goldSetKeys = {
  all: ['gold-set'] as const,
  lists: ['gold-set', 'list'] as const,
  list: (filters: GoldSetFilters) => ['gold-set', 'list', filters] as const,
  detail: (id: string) => ['gold-set', 'detail', id] as const,
  stats: ['gold-set', 'stats'] as const,
  queuedCount: ['gold-set', 'queued-count'] as const,
  mealStatuses: (region: ApiRegion, ids: number[]) => ['gold-set', 'meal-status', region, ids] as const,
};

// A shared queue: poll so claims by others show up, and refresh on return to the tab.
const QUEUE_REFETCH_MS = 30_000;

export const useGoldSetList = (filters: GoldSetFilters, opts: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: goldSetKeys.list(filters),
    queryFn: async () => (await getGoldSetApi()).listItems(filters),
    placeholderData: keepPreviousData,
    refetchInterval: QUEUE_REFETCH_MS,
    refetchOnWindowFocus: true,
    enabled: opts.enabled,
  });

export const useGoldSetItem = (id: string | undefined) =>
  useQuery({
    queryKey: goldSetKeys.detail(id ?? ''),
    queryFn: async () => (await getGoldSetApi()).getItem(id!),
    enabled: !!id,
    // Signed photo URLs last an hour; refetching on focus keeps the lease and claim current.
    refetchOnWindowFocus: true,
  });

/** The nav badge: one row per region, and it stops polling once MNT refuses (no access). */
export const useGoldSetQueuedCount = () =>
  useQuery({
    queryKey: goldSetKeys.queuedCount,
    queryFn: async () => (await getGoldSetApi()).queuedCount(),
    refetchInterval: query => (query.state.status === 'error' ? false : QUEUE_REFETCH_MS * 2),
    retry: false,
  });

export const useGoldSetWeekStats = () =>
  useQuery({
    queryKey: goldSetKeys.stats,
    queryFn: async () => (await getGoldSetApi()).weekStats(),
    refetchInterval: QUEUE_REFETCH_MS * 2,
  });

export const useGoldSetMealStatuses = (region: ApiRegion, ids: number[], enabled = true) =>
  useQuery({
    queryKey: goldSetKeys.mealStatuses(region, ids),
    queryFn: async () => (await getGoldSetApi()).mealStatuses(region, ids),
    enabled: enabled && ids.length > 0,
    placeholderData: keepPreviousData,
  });

/** Every write changes the queue, the stats and possibly gallery badges. */
const useInvalidateQueue = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: goldSetKeys.lists });
    queryClient.invalidateQueries({ queryKey: goldSetKeys.stats });
    queryClient.invalidateQueries({ queryKey: goldSetKeys.queuedCount });
    queryClient.invalidateQueries({ queryKey: ['gold-set', 'meal-status'] });
  };
};

const useItemWrite = <V>(run: (api: GoldSetApi, vars: V) => Promise<GoldSetItem | null>) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: async (vars: V) => run(await getGoldSetApi(), vars),
    onSuccess: item => {
      if (item) {
        queryClient.setQueryData(goldSetKeys.detail(item.id), item);
      }
    },
    onSettled: invalidate,
  });
};

export const useClaimNext = () => useItemWrite<GoldSetClaimNextInput>((api, input) => api.claimNext(input));

export const useClaimItem = () => useItemWrite<string>((api, id) => api.claim(id));

/** Extend and release return a summary; the detail is refetched for its photos. */
const useLeaseWrite = (run: (api: GoldSetApi, id: string) => Promise<unknown>) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: async (id: string) => run(await getGoldSetApi(), id),
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: goldSetKeys.detail(id) });
      invalidate();
    },
  });
};

export const useExtendLease = () => useLeaseWrite((api, id) => api.extend(id));

export const useReleaseItem = () => useLeaseWrite((api, id) => api.release(id));

export const useNominate = () => {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: async (input: GoldSetNominationInput) => (await getGoldSetApi()).nominate(input),
    onSettled: invalidate,
  });
};

export const useWithdrawNomination = () => {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: async (id: string) => (await getGoldSetApi()).withdraw(id),
    onSettled: invalidate,
  });
};

export const useAnnotationSuggestions = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['gold-set', 'suggestions', id],
    queryFn: async () => (await getGoldSetApi()).suggestions(id),
    enabled,
    ...useQueryNeverRefetch,
  });

export const useAnnotationDraft = (id: string) =>
  useQuery({
    queryKey: ['gold-set', 'draft', id],
    queryFn: async () => (await getGoldSetApi()).getDraft(id),
    ...useQueryNeverRefetch,
  });

export const useSaveDraft = (id: string) =>
  useMutation({
    mutationFn: async (draft: AnnotationDraft) => (await getGoldSetApi()).saveDraft(id, draft),
  });

export const useSubmitAnnotation = (id: string) => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: async (draft: AnnotationDraft) => (await getGoldSetApi()).submitAnnotation(id, draft),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['gold-set', 'draft', id] });
      queryClient.invalidateQueries({ queryKey: goldSetKeys.detail(id) });
    },
    onSettled: invalidate,
  });
};

/** The demo catalog when the adapter has one (fixtures); null means "use the real catalog search". */
export const useDemoCatalogSearch = (text: string) =>
  useQuery({
    queryKey: ['gold-set', 'demo-catalog', text],
    queryFn: async () => {
      const api = await getGoldSetApi();
      return api.searchCatalog ? api.searchCatalog(text) : null;
    },
    placeholderData: keepPreviousData,
  });

export const useGoldExplorer = (filters: ExplorerFilters) =>
  useQuery({
    queryKey: ['gold-set', 'explorer', filters],
    queryFn: async () => (await getGoldSetApi()).exploreGold(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });

export const useLastExport = () =>
  useQuery({
    queryKey: ['gold-set', 'last-export'],
    queryFn: async () => (await getGoldSetApi()).lastExport(),
  });
